import type { StateCreator } from 'zustand'
import { mergeListAppName } from '@/lib/appDisplayName'
import { DEFAULT_APP_ICON } from '@/lib/appIcon'
import type { AppInfo } from '@/types'
import { seedAppConfig } from './configSlice'
import { now } from './shared'
import type { AppState } from './types'

/** 应用基础信息：工作台列表 / 应用详情回填 */
export interface AppsSlice {
  apps: AppInfo[]

  getApp: (id: string) => AppInfo | undefined
  createApp: (
    payload: Pick<AppInfo, 'name' | 'description'> & { icon?: string },
  ) => AppInfo
  /** 工作台列表回填：按 id 合并接口结果 */
  syncListApps: (
    items: Array<{ id: string; name: string; icon?: string; module_id?: string }>,
  ) => void
  /** 应用详情回填：按 id 合并 / 插入完整 AppInfo */
  upsertApp: (app: AppInfo) => void
}

/**
 * 详情缺模块 ID 时会退回平台 AppID。已有单独的模块 ID 时保留它，
 * 避免创建后立刻拉详情把侧栏和凭证页刷成 app_…。
 */
function mergeApp(existing: AppInfo, incoming: AppInfo): AppInfo {
  const incomingModule = incoming.module_id
  const keepExisting =
    Boolean(existing.module_id) &&
    existing.module_id !== existing.app_id &&
    (!incomingModule || incomingModule === incoming.app_id)
  return {
    ...existing,
    ...incoming,
    module_id: keepExisting ? existing.module_id : incomingModule || existing.module_id,
  }
}

/** 接口只给了 id / 名称时的本地占位应用 */
export function placeholderApp(
  id: string,
  patch: { name?: string; icon?: string; module_id?: string } = {},
): AppInfo {
  return {
    id,
    name: patch.name || id,
    description: '',
    icon: patch.icon || DEFAULT_APP_ICON,
    app_id: id,
    module_id: patch.module_id || id,
    app_secret: 'sk_****************',
    status: 'development',
    created_at: now(),
  }
}

export const createAppsSlice: StateCreator<AppState, [], [], AppsSlice> = (
  set,
  get,
) => ({
  apps: [],

  getApp: (id) => get().apps.find((a) => a.id === id),

  createApp: (payload) => {
    const id = `app_${Date.now()}`
    const app: AppInfo = {
      id,
      name: payload.name,
      description: payload.description,
      icon: payload.icon ?? DEFAULT_APP_ICON,
      app_id: `cli_${Math.random().toString(16).slice(2, 14)}`,
      app_secret: `sk_${'*'.repeat(16)}${Math.random().toString(16).slice(2, 6)}`,
      status: 'development',
      created_at: now(),
    }
    set((state) => ({
      apps: [...state.apps, app],
      versions: { ...state.versions, [id]: [] },
      ...seedAppConfig(state, id),
    }))
    return app
  },

  syncListApps: (items) => {
    set((state) => {
      const byId = new Map(state.apps.map((a) => [a.id, a]))
      for (const item of items) {
        const existing = byId.get(item.id)
        const module_id = item.module_id || existing?.module_id
        const name = mergeListAppName(existing?.name, item.name, {
          module_id,
          app_id: item.id,
        })
        byId.set(
          item.id,
          existing
            ? {
                ...existing,
                name,
                icon: item.icon || existing.icon,
                module_id: module_id || existing.module_id,
              }
            : placeholderApp(item.id, { ...item, name, module_id }),
        )
      }
      return { apps: Array.from(byId.values()) }
    })
  },

  upsertApp: (app) => {
    set((state) => {
      const idx = state.apps.findIndex((a) => a.id === app.id)
      if (idx < 0) return { apps: [...state.apps, app] }
      const apps = state.apps.slice()
      apps[idx] = mergeApp(apps[idx], app)
      return { apps }
    })
  },
})
